import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  fetchAuthSession,
  login as loginRequest,
  logout as logoutRequest,
  setUnauthorizedHandler,
  type AuthUser,
  type UserRole,
} from './lib/api';

type AuthContextValue = {
  initialized: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  user: AuthUser | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    let active = true;

    setUnauthorizedHandler(() => {
      if (active) {
        setUser(null);
      }
    });

    fetchAuthSession()
      .then((payload) => {
        if (active) {
          setUser(payload.user);
        }
      })
      .catch(() => {
        if (active) {
          setUser(null);
        }
      })
      .finally(() => {
        if (active) {
          setInitialized(true);
        }
      });

    return () => {
      active = false;
      setUnauthorizedHandler(null);
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      initialized,
      async login(username: string, password: string) {
        const payload = await loginRequest(username, password);
        setUser(payload.user);
      },
      async logout() {
        try {
          await logoutRequest();
        } finally {
          setUser(null);
        }
      },
      user,
    }),
    [initialized, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error('AuthContext fehlt.');
  }

  return value;
}

export function isAdminRole(role: UserRole | undefined): boolean {
  return role === 'admin';
}
